import { PaginationState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import KuikaTable, { type KuikaTableColumn } from "../../ui-kit/kuika-table/kuika-table";
import "./code-editor-shell.scss";

/** A column the way a run's response shapes it; the sample `value` decides how it renders. */
export interface ICodeEditorResultColumn {
  title: string;
  dataIndex: string;
  key: string;
  value?: unknown;
}

export interface ICodeEditorResultTableProps {
  rows: object[];
  columns: ICodeEditorResultColumn[];
  /** The drawer's height: it decides how many rows a page holds, so drag it and the pages follow. */
  drawerHeight: number;
  onboardingId?: string;
}

/**
 * A run's row flattened to display strings, one per column and in the columns' order. The raw
 * response rows are shaped by the query, so this is where the dynamic object stops: everything
 * below reads a `string[]` and nothing has to index an open-ended object.
 */
interface IResultRow {
  key: string;
  cells: string[];
}

/**
 * One compact row: the 20px line, the 4px cell padding either side and the row's border. The
 * grid's own `--row-h-table` in code-editor-shell.scss states the same number to the kit, which
 * would otherwise keep its rows on the 40px list rung. Change the two together.
 */
const ROW_HEIGHT: number = 29;
/**
 * The drawer's tab strip (34), the grid's heading row (one row) and its pager (the kit's 48);
 * the rest of the drawer is body. Measured, not rounded down: every pixel this misses by is a
 * row the page holds and the body has no room for, and a page ends a row short of its own count.
 */
const CHROME_HEIGHT: number = 34 + ROW_HEIGHT + 48;
/** A drawer squeezed to its minimum still pages in threes; a one-row page is not worth paging. */
const MIN_PAGE_SIZE: number = 3;

/**
 * Whole rows only, so no drawer height ever leaves half a row showing. Exported because this is
 * the rule the drawer's whole geometry rests on: a page that holds one row more than the body has
 * room for is a row nobody can reach, and it is cheaper to hold that to its arithmetic than to
 * render the grid at every height to find out.
 */
export const rowsPerPage = (drawerHeight: number): number =>
  Math.max(MIN_PAGE_SIZE, Math.floor((drawerHeight - CHROME_HEIGHT) / ROW_HEIGHT));

const formatCell = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "True" : "False";
  if (value === null || value === undefined) return "";
  return String(value);
};

/**
 * What the last run returned. Every cell is one line and a page holds exactly as many rows as
 * the drawer has room for — drag it taller and rows move up from the later pages, drag it
 * shorter and they move back down. Truncated cells still give the full value on hover.
 */
export const CodeEditorResultTable = (props: ICodeEditorResultTableProps): JSX.Element => {
  const [requestedPage, setRequestedPage] = useState<number>(1);

  // A new run is a new result; page 3 of the last one means nothing here.
  useEffect((): void => setRequestedPage(1), [props.rows]);

  const pageSize: number = rowsPerPage(props.drawerHeight);
  const pageCount: number = Math.max(1, Math.ceil(props.rows.length / pageSize));
  // Growing the drawer drops pages off the end, which would otherwise leave the body blank.
  const currentPage: number = Math.min(requestedPage, pageCount);

  const data: IResultRow[] = useMemo((): IResultRow[] => {
    return props.rows.map((row: object, index: number): IResultRow => {
      const byKey: Map<string, unknown> = new Map(Object.entries(row));
      return {
        key: String(index),
        cells: props.columns.map((column: ICodeEditorResultColumn): string => formatCell(byKey.get(column.dataIndex)))
      };
    });
  }, [props.rows, props.columns]);

  const columns: KuikaTableColumn<IResultRow>[] = props.columns.map(
    (column: ICodeEditorResultColumn, index: number): KuikaTableColumn<IResultRow> => ({
      id: column.key,
      header: (): React.ReactNode => (
        <span className="kuika__code-editor__result-table__text" title={column.title}>
          {column.title}
        </span>
      ),
      // `noWrap` keeps the kit from wrapping the cell in its own span, so the truncation and the
      // hover title below are the cell's own — the value a run returns can be far wider than the
      // column, and the reader still needs to see all of it.
      noWrap: true,
      cell: (row: IResultRow): React.ReactNode => (
        <span className="kuika__code-editor__result-table__text" title={row.cells[index]}>
          {row.cells[index]}
        </span>
      )
    })
  );

  const handlePaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)): void => {
    const previous: PaginationState = { pageIndex: currentPage - 1, pageSize };
    const next: PaginationState = typeof updater === "function" ? updater(previous) : updater;
    setRequestedPage(next.pageIndex + 1);
  };

  // A statement that changes rows rather than returning them — an UPDATE, an INSERT — answers
  // with no columns, so there is no grid to draw: say so in the drawer's own one line. The row
  // count is on the Messages tab either way.
  if (props.columns.length === 0) {
    return <div className="kuika__code-editor__drawer__empty">This run returned no rows.</div>;
  }

  // A SELECT or a procedure that matched nothing still names its columns, and those are what
  // the reader came for: the grid keeps its heading and draws no band of its own (the kit's is a
  // 240px panel, taller than the drawer), and the drawer's one line sits centred in the room
  // under the heading, where it stood before the heading was kept. Nothing to page, no pager.
  const hasRows: boolean = data.length > 0;
  const wrapperClassName: string = hasRows
    ? "kuika__code-editor__result-table"
    : "kuika__code-editor__result-table kuika__code-editor__result-table--no-rows";

  // The kit table takes no class of its own, and the onboarding tour anchors on this grid, so
  // both the styling hook and the tour's target live on a wrapper.
  return (
    <div className={wrapperClassName} data-onboarding-id={props.onboardingId}>
      <KuikaTable<IResultRow>
        data-testid="testResultTable"
        data={data}
        columns={columns}
        rowKey={(row: IResultRow): string => row.key}
        // Content sizes the columns and the frame scrolls sideways: a run can return more
        // columns than the drawer is wide, and each one still has to be readable rather than
        // squeezed to nothing.
        layoutMode="auto"
        // The grid IS the drawer body, edge to edge; the drawer's own edges close it.
        frame="flush"
        fitContentHeight
        emptyVariant="none"
        enablePagination={hasRows}
        pageSize={pageSize}
        pagination={{ pageIndex: currentPage - 1, pageSize }}
        onPaginationChange={handlePaginationChange}
      />
      {!hasRows && <div className="kuika__code-editor__drawer__empty">This run returned no rows.</div>}
    </div>
  );
};
